<template>
  <form @submit.prevent novalidate="true">
    <b-card>
      <template slot="header">
        <!-- Client Selection -->
        <div class="form-group form-row mb-0">
          <div class="col-lg-6">
            <treeselect
              :value="clientValue"
              :clearable="false"
              :placeholder="$t('Webportal.ClientFilter.Placeholder')"
              :options="clientItems"
              :disableFuzzyMatching="true"
              :disabled="isBusy"
              @input="treeSelectInput('clientValue', true, ...arguments)"
            />
          </div>
        </div>
      </template>

      <!-- Spinner overlay -->
      <div v-bind:class="{ spinner: isLoading }">

        <!-- Dashboard Tabs -->
        <collapsible-panel class="form-group" :value="true" accordion="cxClientSettings" header="Dashboard Tabs" header-class="h5">
          <b-overlay  :show="isCxDashboardTabLoading" 
                      variant="light"
                      spinner-variant="dark"
                      text="Loading...">
            <div class="mx-1" style="margin-bottom: 85px">
              <b-row>
                <b-col cols="1">
                </b-col>
                <b-col cols="7">
                  <p class="group-title">Name</p>
                </b-col>
                <b-col cols="2">
                  <p class="group-title">Order</p>
                </b-col>
              </b-row>
              
              <div class="my-1" v-for="(tab, index) in getDashboardTabs" :key="index">
                <hr />
                <b-row>
                  <b-col cols="1" style="padding-top: 0.333rem;">
                    <label class="switch switch-label switch-secondary" size="md">
                      <input  class="switch-input"
                              type="checkbox"
                              v-model="tab.IsActive"
                              v-validate
                              :data-vv-name="`DashboardTabActive_${tab.Id}`"
                              :disabled="isBusy || editingDisabled" />
                      <span class="switch-slider" data-checked="On" data-unchecked="Off" />
                    </label>
                  </b-col>

                  <b-col cols="7">
                    <b-form-input type="text"
                                  size="md"
                                  trim
                                  v-model="tab.TranslationString"
                                  :disabled="isBusy || editingDisabled"
                                  v-validate="'required'"
                                  :data-vv-name="`DashboardTabTranslationString_${tab.Id}`" />
                  </b-col>

                  <b-col cols="2">
                    <b-form-select  v-on:change="handleTabSorting($event, tab)"
                                    v-model="tab.SortOrder" 
                                    :options="getDashboardTabSortIds">
                    </b-form-select>
                  </b-col>

                  <b-col cols="2">
                    <b-btn  class="w-100 " size="md"
                            @click="removeCxDashboardTab(tab)" 
                            :disabled="!isDashboardTabDeletable || isBusy || editingDisabled"
                            variant="danger">
                      <b-icon icon="trash" /> {{ $t("Webportal.Delete") }}
                    </b-btn>
                  </b-col>
                </b-row>
              </div>
              <hr />

              <div class="float-right">
                <b-btn  class="add-button"
                        @click="addCxDashboardTabAsync"
                        :disabled="isBusy || editingDisabled || isCxDashboardTabLoading"
                        size="md"
                        variant="secondary">
                  <b-icon icon="plus" /> Dashboard Tab
                </b-btn>
              </div>
            </div>
          </b-overlay>
        </collapsible-panel>
        <!-- Categories -->
        <collapsible-panel class="form-group" accordion="cxClientSettings" :header="$t('Webportal.CX.Administration.Categories')" header-class="h5">
          <div class="mx-1" style="margin-bottom: 85px" :key="categoriesKey">
            <b-row>
              <b-col cols="1">
              </b-col>
              <b-col cols="4">
                <p class="group-title">Name</p>
              </b-col>
              <b-col cols="3">
                <p class="group-title">Dashboard Tab</p>
              </b-col>
              <b-col cols="2">
                <p class="group-title">Order</p>
              </b-col>
            </b-row>
            
            <!-- Category list -->
            <div class="my-1" v-for="(category, index) in getCategories" :key="index">
              <hr />
              <b-row>
                <!-- IsActive -->
                <b-col cols="1" style="padding-top: 0.33rem;">
                  <label class="switch switch-label switch-secondary">
                    <input  class="switch-input"
                            type="checkbox"
                            v-model="category.IsActive"
                            v-validate
                            :data-vv-name="`CategoryActive_${category.Id}`"
                            :disabled="isBusy || editingDisabled" />
                    <span class="switch-slider" data-checked="On" data-unchecked="Off" />
                  </label>
                </b-col>

                <!-- TranslationString -->
                <b-col cols="4">
                  <b-form-input type="text"
                                size="md"
                                trim
                                v-model="category.TranslationString"
                                :disabled="isBusy || editingDisabled"
                                v-validate="'required'"
                                :data-vv-name="`CategoryTranslationString_${category.Id}`" />
                </b-col>

                <b-col cols="3">
                  <b-form-select  v-model="category.CxDashboardTabId"
                                  v-validate="'required'"
                                  :data-vv-name="`CategoryDashboardTab_${category.CxDashboardTabId}`"
                                  :options="getDashboardTabsForCategories">
                  </b-form-select>
                </b-col>

                <!-- Sorting -->
                <b-col cols="2">
                  <b-form-select  v-on:change="handleCategorySorting($event, category)"
                                  v-model="category.SortOrder"
                                  v-validate="'required'"
                                  :data-vv-name="`CategorySortOrder_${category.SortOrder}`"
                                  :options="getCategorySortIds">
                  </b-form-select>
                </b-col>

                <!-- Delete button -->
                <b-col cols="2">
                  <button type="button"
                          @click="removeCategory(category)"
                          class="btn btn-danger w-100"
                          :disabled="!isCategoryDeletable || isBusy || editingDisabled">
                    <b-icon icon="trash" /> {{ $t("Webportal.Delete") }}
                  </button>
                </b-col>
              </b-row>
            </div>
            <hr />

            <!-- Add Categories -->
            <div class="float-right">
              <b-btn  class="add-button"
                      @click="addCategory"
                      :disabled="isBusy || editingDisabled"
                      size="md"
                      variant="secondary">
                <b-icon icon="plus" /> {{ $t("Webportal.CX.Administration.Category") }}
              </b-btn>
            </div>
          </div>
        </collapsible-panel>

        <!-- APIs -->
        <collapsible-panel class="form-group" accordion="cxClientSettings" :header="$t('Webportal.CX.Apis')" header-class="h5">
          <div class="mx-1" style="margin-bottom: 85px">
            <b-row>
              <b-col cols="1">
              </b-col>
              <b-col cols="3">
                <p class="group-title">Name</p>
              </b-col>
              <b-col cols="3">
                <p class="group-title">{{ $t("Webportal.CX.FirstAid") }}</p>
              </b-col>
              <b-col cols="3">
                <p class="group-title">Category</p>
              </b-col>
            </b-row>
            
            <div class="my-1" v-for="(api, index) in apiSettings" :key="index">
              <hr />
              <b-row>
                <!-- Name (not changeable) -->
                <b-col cols="1"></b-col>
                <b-col cols="3" class="py-1">{{ api.Name }}</b-col>

                <!-- FirstAidKit selector -->
                <b-col cols="3">
                  <treeselect
                    v-model="api.FirstAidKitGroup"
                    :clearable="true"
                    :placeholder="$t('Webportal.CX.FirstAidKit.Placeholder')"
                    :options="firstAidKitGroupItems"
                    :disableFuzzyMatching="true"
                    :disabled="isBusy || editingDisabled"
                    v-validate="'required'"
                    :data-vv-name="`ApiFirstAidKitGroup_${api.ApiID}`"
                  />
                </b-col>

                <b-col cols="3">
                  <treeselect v-model="api.ApiCategoryID"
                              :clearable="true"
                              placeholder="Category..."
                              :normalizer="normalizeCategories"
                              :options="getCategories"
                              :disableFuzzyMatching="true"
                              :disabled="isBusy || editingDisabled"
                              v-validate="'required'"
                              :data-vv-name="`ApiCategoryID_${api.ApiID}`" />
                </b-col>
              </b-row>
            </div>
            <hr />

          </div>
        </collapsible-panel>

        <!-- KPIs -->
        <collapsible-panel class="form-group" accordion="cxClientSettings" :header="$t('Webportal.Kpis')" header-class="h5">
          <div class="mx-1">
            <Accordion v-for="(kpiGroup, index) in kpiGroups" :key="index" :toggleCols="1" groupId="KPI" class="mb-4">
              <template slot="short">
                <h6>{{ kpiGroup.KpiGroupName }}</h6>
              </template>
              <template slot="long">
                <div v-for="(kpi, index) in kpiGroup.KPIs" :key="index">
                  <b-row>
                    <!-- Kpi Name -->
                    <b-col>
                      {{ kpi.KpiName }}
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Kpi Question selector -->
                    <b-col>
                      <treeselect
                        v-model="kpi.ShowAnswerFromExternalSurveyQuestionId"
                        :clearable="true"
                        :placeholder="$t('Webportal.CX.Administration.KpiQuestionPlaceholder')"
                        :options="kpi.AvailableQuestions || []"
                        :disableFuzzyMatching="true"
                        :disabled="isBusy || editingDisabled"
                        v-validate="'required'"
                        :data-vv-name="`KpiQuestion_${kpi.KpiId}`"
                      >
                        <div slot="value-label" slot-scope="{ node }">
                          {{ node.raw.label }}
                          <span class="sub-text">
                            {{ node.raw.id }}
                          </span>
                        </div>

                        <label slot="option-label" slot-scope="{ node }">
                          {{ node.raw.label }}
                          <span class="sub-text">
                            {{ node.raw.id }}
                          </span>
                        </label>
                      </treeselect>
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Kpi Formatting -->
                    <b-col>
                      <treeselect
                        :value="kpi.Formatting || 0"
                        @input="kpi.Formatting = $event"
                        :clearable="true"
                        :options="kpiFormattingItems"
                        :disableFuzzyMatching="true"
                        :disabled="isBusy || editingDisabled"
                        v-validate="'required'"
                        :data-vv-name="`KpiFormatting_${kpi.KpiId}`"
                      >
                        <div slot="value-label" slot-scope="{ node }">
                          {{ node.raw.label }}
                          <span class="sub-text">
                            {{ node.raw.example }}
                          </span>
                        </div>

                        <label slot="option-label" slot-scope="{ node }">
                          {{ node.raw.label }}
                          <span class="sub-text">
                            {{ node.raw.example }}
                          </span>
                        </label>
                      </treeselect>
                    </b-col>

                    <!-- Kpi Hide Popup setting -->
                    <b-col class="mt-3" cols="4">
                      <b-row>
                        <b-col cols="2" class="p-0">
                          <label class="switch switch-label switch-secondary">
                            <input
                              class="switch-input"
                              type="checkbox"
                              v-model="kpi.HidePopup"
                              v-validate
                              :data-vv-name="`KpiHidePopup_${kpi.KpiId}`"
                              :disabled="isBusy || editingDisabled"
                            />
                            <span
                              class="switch-slider"
                              data-checked="On"
                              data-unchecked="Off"
                            />
                          </label>
                        </b-col>

                        <b-col class="mt-n2 p-1">
                          {{ $t("Webportal.CX.Administration.HidePopup") }}
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-row>

                  <b-row>
                    <!-- Kpi Logo -->
                    <b-col>
                      <image-uploader :ref="`kpiLogoUploader_${kpi.KpiId}`"
                        :value="getLogoUrl(kpi)"
                        @input="setLogoUrl(afs, $event)"
                        :upload-url="getKpiLogoUploadUrl(kpi)"
                        :previewHeight="35"
                        v-validate="'required'"
                        :data-vv-name="`KpiLogo_${kpi.KpiId}`"
                      />
                    </b-col>
                  </b-row>

                  <hr />
                </div>
              </template>
            </Accordion>
          </div>
        </collapsible-panel>

        <!-- AFS -->
        <collapsible-panel class="form-group" accordion="cxClientSettings" :header="$t('Webportal.CX.Administration.AFS')" header-class="h5">
          <div class="mx-1">
            <div class="my-1" v-for="(afs) in afsSettings" :key="afs.SurveyId">
              <b-row>
                <!-- Name (not changeable) -->
                <b-col cols="5">
                  {{ afs.Name }}
                </b-col>

                <!-- Category selector -->
                <b-col>
                  <treeselect
                    v-model="afs.Category"
                    :clearable="true"
                    :delete-removes="false"
                    :backspace-removes="false"
                    :placeholder="$t('Webportal.CX.Administration.Category.Placeholder')"
                    :options="getCategories"
                    :normalizer="normalizeCategories"
                    :disableFuzzyMatching="true"
                    :disabled="isBusy || editingDisabled"
                    v-validate="'required'"
                    :data-vv-name="`AfsCategory_${afs.SurveyId}`"
                  />
                </b-col>

                <!-- Delete button -->
                <b-col cols="2">
                  <button
                    type="button"
                    @click="removeExploration(afs)"
                    class="btn btn-danger w-100"
                    :disabled="isBusy || editingDisabled"
                  >
                    {{ $t("Webportal.Delete") }}
                  </button>
                </b-col>
              </b-row>

              <!-- Image uploader -->
              <b-row>
                <b-col>
                  <image-uploader :ref="`afsLogoUploader_${afs.SurveyId}`"
                    :value="getLogoUrl(afs)"
                    @input="setLogoUrl(afs, $event)"
                    :upload-url="getAfsLogoUploadUrl(afs)"
                    :previewHeight="35"
                    v-validate="'required'"
                    :data-vv-name="`AfsLogo_${afs.SurveyId}`"
                  />
                </b-col>
              </b-row>

              <!-- FirstAidKit selector -->
              <b-row style="margin-top: -40px">
                <b-col cols="3" style="z-index:-1"/>
                <b-col class="text-right p-0" style="font-weight: 600">
                  {{ $t("Webportal.CX.FirstAidKit") }}:
                </b-col>
                <b-col cols="7">
                  <treeselect
                    v-model="afs.FirstAidKitGroup"
                    :clearable="true"
                    :placeholder="$t('Webportal.CX.FirstAidKit.Placeholder')"
                    :options="firstAidKitGroupItems"
                    :disableFuzzyMatching="true"
                    :disabled="isBusy || editingDisabled"
                    v-validate="'required'"
                    :data-vv-name="`FirstAidKitGroup_${afs.SurveyId}`"
                  />
                </b-col>
              </b-row>

              <hr class="my-5" />
            </div>

            <hr />

            <!-- Add Surveys -->
            <b-row>
              <b-col>
                <treeselect
                  v-model="addSurveyValue"
                  :clearable="false"
                  :placeholder="$t('Webportal.SurveyFilter.Placeholder')"
                  :options="unusedSurveyItems"
                  :disableFuzzyMatching="true"
                  :disable-branch-nodes="true"
                  :disabled="isBusy || editingDisabled"
                />
              </b-col>

              <b-col cols="auto">
                <button
                  type="button"
                  @click="addExploration()"
                  class="btn btn-secondary w-100"
                  :disabled="!isSurveySelected || isBusy || editingDisabled"
                >
                  + {{ $t("Webportal.CX.Administration.AFS") }}
                </button>
              </b-col>
            </b-row>
          </div>
        </collapsible-panel>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button type="button"
                  @click="submit"
                  class="btn btn-primary"
                  :disabled="!isFormChanged || !isFormValid || isBusy || editingDisabled">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import CancelToken from "@/mixins/CancelToken";

import AdministationService from "@/services/AdministationService";
import SurveysService from "@/services/SurveysService";

import Accordion from "../Accordion";
import CollapsiblePanel from '@/components/CollapsiblePanel';
import ImageUploader from "./ImageUploader";
import NumbersMixin from '@/mixins/NumbersMixin.js';

export default {
  name: "CXClientSettings",

  mixins: [AppConfig, CancelToken, NumbersMixin],

  components: {
    Accordion,
    CollapsiblePanel,
    ImageUploader
  },

  data() {
    return {
      firstAidKitGroups: [],

      clientItems: [],
      clientValue: null,

      addSurveyValue: null,

      addedDashboardTabsCount: 0,
      dashboardTabs: [],
      removedCxDashboardTabIds: [],
      isCxDashboardTabLoading: false,

      categoriesChanged: false,
      addedCategoriesCount: 0,
      categoriesKey: 0,
      categories: [],
      removedCategoryIds: [],

      apiSettings: [],
      
      afsSettings: [],
      removedAfsSettings: [],
      
      kpiGroups: [],

      cancelToken: null,

      isBusy: false,
      isLoading: false,

      kpiFormattingItems: [
        { id: 0, label: this.$t('Webportal.CX.Administration.Formatting.Percentage'),   example: "xx%" },
        { id: 1, label: this.$t('Webportal.CX.Administration.Formatting.None'),         example: "xx" },
        { id: 2, label: this.$t('Webportal.CX.Administration.Formatting.Time'),         example: "xx:yy h" },
        { id: 3, label: this.$t('Webportal.CX.Administration.Formatting.Time_Dynamic'), example: "xx Min || xx:yy h" },
        { id: 4, label: this.$t('Webportal.CX.Administration.Formatting.Time_Minutes'), example: "xx Min" },
        { id: 5, label: this.$t('Webportal.CX.Administration.Formatting.Time_Hours'),   example: "< 1 h || xx h" }
      ]
    };
  },

  mounted() {
    this.initializeTheme();
  },

  computed: {
    editingDisabled() {
      return _.isNil(this.clientValue);
    },
    
    isDashboardTabDeletable() {
      return true;
    },
    getDashboardTabs() {
      return _.sortBy(this.dashboardTabs, 'SortOrderFix');
    },
    getDashboardTabSortIds() {
      return _.sortBy(this.dashboardTabs.map(x => x.SortOrder));
    },
    getDashboardTabsForCategories() {
      let options = this.dashboardTabs.map(x => ({ value: x.Id, text: x.TranslationString, SortOrderFix: x.SortOrderFix }));
      options = _.sortBy(options, 'SortOrderFix');
      options.unshift({ value: null, text: 'not assigned' });
      return options;
    },

    isCategoryDeletable() {
      return this.categories.length > 0;
    },
    getCategories() {
      return _.sortBy(this.categories, 'SortOrderFix');
    },
    getCategorySortIds() {
      let items = _.sortBy(this.categories.map(x => x.SortOrder));
      return items;
    },

    isSurveySelected() {
      return !!this.addSurveyValue;
    },

    isFormChanged() {
      return this.categoriesChanged ||
        this.removedCategoryIds?.length > 0 ||
        this.removedAfsSettings?.length > 0 ||
        this.removedCxDashboardTabIds.length > 0 ||
        Object.keys(this.validationFields).some(
          key => this.validationFields[key].dirty || this.validationFields[key].changed
        );
    },

    isFormValid() {
      return Object.keys(this.validationFields).every(
        key => (this.validationFields[key].valid || null) !== undefined
      );
    },

    firstAidKitGroupItems() {
      return this.firstAidKitGroups?.map(fakg =>
        ({
          id:     fakg.groupId,
          label:  fakg.groupName
        })
      ) || [];
    },

    unusedSurveyItems() {
      const usedSurveys = this.afsSettings?.map(afs => afs.SurveyId?.toString());
      const currentClient = _.find(this.clientItems, { id: this.clientValue });

      if (currentClient?.projects?.length > 0) {
        return currentClient.projects.map(p => 
          ({
            ...p,
            id: 'P' + p.id,
            children: p.surveys?.map(s =>
              ({
                ...s,
                isDisabled: usedSurveys?.indexOf(s.id) > -1
              })
            )
          })
        ) || [];
      }

      return currentClient?.surveys?.map(s => ({
        ...s,
        isDisabled: usedSurveys.indexOf(s.id) > -1
      })) || [];
    }
  },

  activated() {
    this.getSurveyStructure();
  },

  beforeRouteLeave(to, from, next) {
    this.$emit("unsavedChanges", this.isFormChanged, next);
  },

  methods: {
    normalizeCategories(node) {
      return {
        id: node.Id,
        label: node.TranslationString
      };
    },

    handleTabSorting(event, tab) {
      this.dashboardTabs = this.updateSortOrder(this.dashboardTabs, tab, event);
      this.categoriesChanged = true;
    },

    handleCategorySorting(event, cat) {
      this.categories = this.updateSortOrder(this.categories, cat, event);
      this.categoriesKey++;
      this.categoriesChanged = true;
    },
    
    updateSortOrder(items, item, newSortOrder) {
     // Remove the item from its old position
      items = items.filter(x => x.Id !== item.Id);
      // apply new sort order
      item.SortOrder = newSortOrder;
      // Insert the item at its new position
      items.splice(newSortOrder - 1, 0, item);
      // Renumber all items' SortOrder and SortOrderFix
      for (let i = 0; i < items.length; i++) {
        items[i].SortOrder = i + 1;
        items[i].SortOrderFix = i + 1;
      }
      return items;
    },
    
    setLogoUrl(obj, value) {
      if (!value) obj.ExtLogoId = null;
    },

    getLogoUrl(obj) {
      return obj.ExtLogoId ?
        process.env.BASE_URL + `api/media/image?id=${obj.ExtLogoId}`:
        null;
    },

    getKpiLogoUploadUrl(kpi) {
      return `api/administration/kpi-logo?kpi-id=${kpi.KpiId}`;
    },

    getAfsLogoUploadUrl(afs) {
      const url = 'api/administration/afs-logo?';
      return afs.ExternalSurveyId ?
        url + `external-survey-id=${afs.ExternalSurveyId}` :
        url + `survey-id=${afs.SurveyId}`;
    },

    // API: SurveysService.getSurveyStructure ... Get survey structure data to extract all clients
    getSurveyStructure() {
      this.isLoading = true;

      return SurveysService.getSurveyStructure(null, true).then(response => {
        // Filter clients
        this.clientItems = response.data
          ?.filter(c => c.id?.startsWith('C'))
          ?.map(c => this.surveyStructureParse(c));

        if (this.clientValue === null) {
          this.treeSelectInput('clientValue', true, this.clientItems[0].id);
        }
      })
      .finally(() => { 
        if (this.clientValue !== null)
          this.isLoading = false; 
      });
    },

    surveyStructureParse(parent) {
      if (!parent) return;
      return {
        id: parent.id?.substring(1),
        label: parent.label,
        surveys: parent.children?.filter(s => s.id?.startsWith('S')).map(s => this.surveyStructureParse(s)),
        projects: parent.children?.filter(p => p.id?.startsWith('P') && p.children).map(p => this.surveyStructureParse(p))
      };
    },

    // API: AdministationService.getCXClientSettings
    getSettings(hotSwap) {
      if (!this.clientValue) return;

      this.isLoading = true;
      this.isBusy = true;

      if (!hotSwap) {
        this.firstAidKitGroups = [];
        this.dashboardTabs = [];
        this.categories = [];
        this.apiSettings = [];
        this.afsSettings = [];
        this.kpiGroups = [];
      }

      this.addedDashboardTabsCount = 0;
      this.addedCategoriesCount = 0;
      this.removedCxDashboardTabIds = [];
      this.removedAfsSettings = [];
      this.removedCategoryIds = [];

      this.cancelToken = this.initCancelToken(this.cancelToken);

      return AdministationService.getCXClientSettings(this.clientValue, this.cancelToken)
        .then(response => {
          if (!response.data) {
            console.warn('Got empty response for settings.');
            return;
          }
          // initialize all item-lists
          this.firstAidKitGroups = response.data.FirstAidKitGroups || [];
          this.dashboardTabs = this.initDashboardTabs(response.data.DashboardTabs || []); 
          this.categories = this.initCategories(response.data.Categories || []);
          this.apiSettings = _.orderBy(response.data.APIs || [], 'ApiID');
          this.afsSettings = response.data.APIsFromSurveys || [];
          this.kpiGroups = response.data.KPIGroups || [];

          this.$validator.reset();
        })
        .catch(error => console.warn("Error while loading cx client settings: " + error))
        .finally(() => {
          this.isLoading = false;
          this.isBusy = false;
          this.cancelToken = null;
        });
    },
    
    initDashboardTabs(items) {
      // mirrored field for sorting is needed for the functioning of 
      // switching the order via dropdown list
      for (let i = 0; i < items.length; i++) {
        items[i].SortOrderFix = items[i].SortOrder;
      }
      return items;
    },

    /* this is necessary, to handle -1 values from the database
     * the field in the database had to be added with NOT NULL 
     * to introduce less of a pain on database. */
    initCategories(items) {
      // get count of items that have -1 as SortOrder
      let count = items.filter(x => x.SortOrder < 1).length;
      // if count > 0, we have to renumber all items that have SortOrder === -1
      if (count > 0) {
        this.categoriesChanged = true;
        // get max SortOrder, if max < 0 then set max to 0
        let max = Math.max(...items.map(x => x.SortOrder));
        if (max < 1) max = 0;

        // renumber all items that have SortOrder === -1
        for (let i = 0; i < items.length; i++) {
          if (items[i].SortOrder < 1) {
            items[i].SortOrder = ++max;
          }
        }
      }

      // mirrored field for sorting is needed for the functioning of 
      // switching the order via dropdown list
      for (let i = 0; i < items.length; i++) {
        items[i].SortOrderFix = items[i].SortOrder;
      }

      return items;
    },

    // Called whenever the input of the structure treeselect changes
    treeSelectInput(valueField, getSettings, value) {
      if (this[valueField] === value) 
        return;

      let currentValue = this[valueField];
      this.$emit("unsavedChanges", this.isFormChanged, result => {
        // Cancel
        if (result === false) {
          this[valueField] = currentValue;
        }
        // User discarded changes or form is not dirty
        else if (result == null) {
          this[valueField] = value;
        }
      });

      if (getSettings)
        this.getSettings();
    },

    cancel() {
      this.$emit("unsavedChanges", this.isFormChanged, result => {
        if (result !== false) {
          //this.getSettings();
        }
      });
    },

    uploadLogos(type) {
      _.forEach(this.validationFields, (val, name) => {
        // Check if dirty and logo upload
        if (!val.dirty || !name.startsWith(`${_.capitalize(type)}Logo_`)) return;

        // Extract and check elements id
        const id = name.split('_')[1];
        if (!this.isNumeric(id)) return;

        // Get component from $refs (as used in v-for it is an array)
        const ref = this.$refs[`${type.toLowerCase()}LogoUploader_${id}`]?.[0];
        if (!ref) return;

        ref.uploadImage();
      });
    },

    // API: AdministationService.setCXClientSettings
    submit() {
      this.isBusy = true;
      this.cancelToken = this.initCancelToken(this.cancelToken);
      let payload = {
          clientValue: this.clientValue,
          Categories: this.categories.map(item => ({
            Id: item.Id, 
            ClientId: item.ClientId, 
            IsActive: item.IsActive,
            CxDashboardTabId: item.CxDashboardTabId,
            SortOrder: item.SortOrder,
            TranslationString: item.TranslationString })),
          CxDashboardTabs: this.dashboardTabs.map(item => ({
            Id: item.Id,
            ClientId: item.ClientId, 
            IsActive: item.IsActive,
            SortOrder: item.SortOrder,
            TranslationString: item.TranslationString })),
          RemovedCxDashboardTabIds: this.removedCxDashboardTabIds,
          RemovedCategoryIds: this.removedCategoryIds,
          RemovedAfsIds: this.removedAfsSettings?.map(afs => afs.ExternalSurveyId),
          ApiFromSurveySettings: this.afsSettings,
          ApiSettings: this.apiSettings,
          KpiSettings: _.flatMap(this.kpiGroups, group => group.KPIs?.map(kpi => _.omit(kpi, "AvailableQuestions")))
        };

      AdministationService.setCXClientSettings(
        payload,
        this.cancelToken
      )
      // Upload logos after settings are done (ensures elements are all set)
      .then(() => this.uploadLogos("afs"))
      .then(() => this.uploadLogos("kpi"))
      // Newly added items get ids - we have to use them, if the user again decides to update something
      .then(() => this.getSettings(true))
      .then(() => this.$validator.reset())
      .catch(e => {
        console.error("Failed to store settings.", e);
        this.$emit("saveError", result => {});
      })
      .finally(() => {
        this.isBusy = false;
        this.categoriesChanged = false;
        this.cancelToken = null;
      });
    },
    
    // promise is a test balloon for adding an item with a loading indicator,
    // when it takes a bit longer
    async addCxDashboardTabAsync() {
      this.isCxDashboardTabLoading = true;
      try {
        await new Promise(resolve => {
          this.dashboardTabs.push({
            Id: (++this.addedDashboardTabsCount) * -1,
            TranslationString: "My new Tab",
            IsActive: true,
            SortOrder: this.dashboardTabs.length + 1,
            SortOrderFix: this.dashboardTabs.length + 1
          });
          resolve();
        });
      } finally {
        this.$nextTick(() => {
          this.isCxDashboardTabLoading = false;
        });
      }
    },

    removeCxDashboardTab(tab) {
      if (tab.Id >= 0) {
        this.removedCxDashboardTabIds.push(tab.Id);
      }
      
      this.dashboardTabs = this.dashboardTabs.filter(x => x.Id !== tab.Id);

      for (let i = 0; i < this.categories.length; i++) {
        if (this.categories[i].CxDashboardTabId === tab.Id) {
          this.categories[i].CxDashboardTabId = null;
        }
      }
    },

    addCategory() {
      const newId = (++this.addedCategoriesCount) * -1;
      let sort = this.categories.length + 1;
      this.categories.push({
        Id: newId, 
        IsActive: true,
        CxDashboardTabId: null,
        SortOrder: sort,
        SortOrderFix: sort,
        TranslationString: this.$t("Webportal.CX.Administration.Category.TextPlaceholder")
      });

      // Allow user to acutally store new category
      this.$nextTick(() =>
        this.$validator.flag(`CategoryTranslationString_${newId}`, { dirty: true })
      );
    },

    removeCategory(category) {
      // Only database items have to be deleted in backend
      // They are identified by positive ids
      if (category.Id >= 0) {
        this.removedCategoryIds.push(category.Id);
      }

      // Remove element from frontend (use splice to engage watchers)
      const position = this.categories.indexOf(category);
      if (position < 0) return;
      this.categories.splice(position, 1);

      // Change all categories of AFS using this category
      // Set null as fallback Category.Id;
      for (let i = 0; i < this.afsSettings.length; ++i) {
        const afs = this.afsSettings[i];
        if (afs.Category === category.Id) {
          this.$set(this.afsSettings, i, { ...afs, Category: null });
        }
      }
    },

    addExploration() {
      // Restore deleted element, if present
      const deletedSurveyIndex = this.removedAfsSettings.findIndex(
        afs => afs.SurveyId == this.addSurveyValue
      );

      if (deletedSurveyIndex > -1) {
        this.afsSettings.push(this.removedAfsSettings[deletedSurveyIndex]);
        this.removedAfsSettings.splice(deletedSurveyIndex, 1);
      }
      else {
        // Find selected survey in survey structure
        let survey = null;
        const tester = (s) => { if (s.id == this.addSurveyValue) return (survey = s); }
        this.unusedSurveyItems.some(p => 
          // if there are no children, the projects layer was skipped
          p?.children ? p.children?.some(s => tester(s)) : tester(p)
        );

        if (!survey)
          return console.error(`Can not find survey with id '${this.addSurveyValue}' in survey structure!`);

        this.afsSettings.push(
          {
            Category: _.minBy(this.categories, 'Id').Id,
            Name: survey.label,
            SurveyId: survey.id
          }
        );
      }

      // Allow user to acutally store new category
      this.$nextTick(() => {
        this.$validator.flag(`AfsCategory_${this.addSurveyValue}`, { dirty: true })
        
        // Unselect added survey
        this.addSurveyValue = null;
      });
    },

    removeExploration(afs) {
      const index = this.afsSettings.indexOf(afs);
      if (index < 0)
        return console.error(`Can not find survey with id '${afs.SurveyId}' in AFS settings.`);

      // Store deleted AFS, if it is present in database (has ExternalSurveyId)
      if (afs.ExternalSurveyId)
        this.removedAfsSettings.push(afs);

      // Use splice for removal to satisfy watcher
      this.afsSettings.splice(index, 1);
    }
  }
};
</script>

<style scoped>
.group-title {
  font-size: 12pt;
  font-weight: 600;
  margin-bottom: 0rem;
}

.sub-text {
  color: #bbb;
  font-style: italic;
}

hr {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.add-button {
  min-width: 146px;
}
</style>
